import { MpiStatus } from 'utility/constants';

const DISCOUNT_FIXED = 'fixed' as const;
const DISCOUNT_PERCENT = 'percent' as const;
const CHARGE_FIXED = 'fixed' as const;
const CHARGE_PERCENT = 'percent' as const;
type DiscountType = typeof DISCOUNT_FIXED | typeof DISCOUNT_PERCENT;
type ChargeType = typeof CHARGE_FIXED | typeof CHARGE_PERCENT;

export type Discount = {
  /** id will exist if the object is persisted in the BE */
  id?: number;
  name: string;
  type: DiscountType;
  amount: number;
  maxDiscount: string | null;
  addToEveryQuote?: boolean;
  taxable: boolean;
};

export type Charge = {
  /** id will exist if the object is persisted in the BE */
  id?: number;
  name: string;
  type: ChargeType;
  amount: number;
  minCharge: string | null;
  maxCharge: string | null;
  addToEveryQuote?: boolean;
  taxable: boolean;
};

export type ServiceGroup = {
  id: number;
  name: string;
  isDefault: boolean;
  appointmentCodes: AppointmentCode[];
  appointmentServices: [];
};

export const APPOINTMENT_PDF_SIGNATURE_DOC_TYPE = {
  APPOINTMENT: 'workOrder',
  WALK_IN: 'workOrder',
  CHECK_OUT: 'invoice',
} as const;

export type PDFSignatureDocType =
  (typeof APPOINTMENT_PDF_SIGNATURE_DOC_TYPE)[keyof typeof APPOINTMENT_PDF_SIGNATURE_DOC_TYPE];

export enum LyWindow {
  JOBS = 'jobs',
  ESTIMATOR = 'estimator',
}

export type SignablePdfData = {
  base64: string;
  signatureCoordinates: {
    left: number;
    top: number;
    height: number;
    width: number;
    page: number;
  }[];
};

export type NonEmptyString<T> = T extends '' ? never : T;

export type ApiResponse<T> = {
  results: T[];
  totalRefunded: number;
  totalResults: number;
  totalPages: number;
  previous: string;
  currentPage: number;
  next: string;
};

// a walk-in is a repair order without an appointment
export type WalkIn = Omit<RepairOrder, 'appointments'> & {
  isWalkIn: true;
  appointments: [];
};

export type Appointment = {
  id: number;
  guestUser: GuestUser | null;
  user: User | null;
  customer: Customer;
  alternativeTransportationType: TransportationType | null;
  repairOrder: RepairOrder | null;
  dateCreated: Date;
  date: string;
  textUpdates: boolean;
  emailUpdates: boolean;
  vehicleYear: number;
  vehicleModel: string;
  vehicleMake: string;
  miles: number | null;
  vin: string | null;
  complaint: string | null;
  internalNotes: string | null;
  deleted: boolean;
  appointmentServices: AppointmentService[];
  otherRequest: string | null;
  dmsId: string | null;
  vehicleDmsId: string | null;
  source?: 'DMS' | 'Member' | 'Customer'; // set by API
  servicesInformation: AppointmentService[];
};

export type Recall = {
  component: string | null;
  consequence: string | null;
  defaultOperationCode: OperationCode;
  description: string;
  manufacturer: string | null;
  nhtsaNumber: string;
  operationCodes: OperationCode[];
  recallNumber: string;
  remedy: string | null;
  reportDate: string | null;
  status: string;
  summary: string | null;
  unitsEffected: number | null;
};

export type Recalls = Recall[];

export enum ServiceLanes {
  body = 'body',
  express = 'express',
  main = 'main',
  none = 'none',
}

export type AppointmentCode = {
  id: number;
  title: string;
  code: string;
  description: string;
  laborHours: number;
  customerHours: number;
  cost: number;
  serviceLane: string;
  deleted: boolean | null;
  internal: boolean;
  sortOrder: number | null;
  serviceGroup: ServiceGroup | null;
  serviceGroupId?: number | null;
  maxAllowedPerDay?: number | null;
  appointmentCodeGroup: AppointmentCodeGroup;
  appointmentCodeGroupId?: number;
};

export type AppointmentCodeOperationCode = {
  id: number;
  appointmentCode: AppointmentCode | null;
  operationCode: OperationCode | null;
};

export type AppointmentCodeServicePayload = Partial<AppointmentService>;

export type AppointmentService = {
  type: 'operationCode' | 'appointmentCode';
  code: string;
  complaint: string | null;
  description: string;
  dmsId: number | null;
  hours: string;
  id: number;
  internalNotes: string | null;
  serviceLane: ServiceLanes;
};

export type AppointmentCodeGroup = {
  id: number;
  name: string;
  sortOrder: number;
  appointmentCodes: AppointmentCode[] | null;
  isDefault: boolean;
};

export type Coupon = {
  id: number;
  title: string;
  image: string | null;
};

//TODO: there are currently two types of CheckIn, will change these to be more specific once know more info
export type CheckIn1 = {
  image: string | null;
  price: number | null;
  costPerMessage: number | null;
  sent: any[];
  unsent: any[];
};

export type CheckIn2 = {
  id: number;
  appointment: Appointment;
  customer: Customer;
  user: User;
  customerVehicle: CustomerVehicle;
  repairOrder: RepairOrder;
  date: string;
  guestUser: GuestUser;
};

export type CheckInMedia = {
  id: number;
  checkIn: CheckIn2;
  isVideo: boolean;
  mediaUrl: string;
  date: string;
};

export type CheckInOperationCode = {
  id: number;
  user: User;
  checkIn: CheckIn2;
  laborRate: LaborRate;
  operationCode: OperationCode;
  date: string;
};

export type Customer = {
  id: number;
  name: string;
  phone: string | null;
  mobileConfirmed: boolean;
  email: string | null;
  doNotContact: boolean;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  customerRewards: CustomerRewards;
  marketingOptOut: boolean;
  customerVehicles: CustomerVehicle[] | null;
  dateLastContacted: Date | null;
  dateLastVisited: Date | null;
  unread: number;
  dmsId: string | null;
};

export type CustomerNote = {
  id: number;
  user: User | null;
  note: string;
  date: Date;
};

export type CustomerVehicle = {
  id: number;
  year: number;
  make: string;
  model: string;
  miles: number | null;
  vin: string | null;
};

export type CustomerRewards = {
  dateFirstEarned: string;
  dateLastEarned: string;
  points: number;
  totalEarned: number;
  totalSpent: number;
  rewards: number;
};

export type Demo = {
  id: number;
  createdByUser: User | null;
  assignedUser: User | null;
  customer: Customer;
  repairOrder: RepairOrder;
  referenceDemo: Demo | null;
  name: string;
  phone: string;
  email: string | null;
  status: string;
  dateCreated: Date;
  dateUpdated: Date;
};

export type DropdownOption = {
  label: string;
  value: any;
};

export type Event = {
  id: number;
  name: string;
  className: string;
  entityId: number;
  eventType: string;
  date: Date;
  user: User | null;
};

export type Estimate = {
  notes: any[];
  laborTime: number;
  requiredSkill: any;
  serviceType: string;
  severeCondition: boolean;
  name: string;
  frequencyType: string;
  intervalMiles: number;
  intervalMonths: number;
};

export type GuestUser = {
  id: number;
  dateCreated: Date | null;
  totalRequestCount: number | null;
  updateRequestCount: number | null;
  appointments: Appointment[];
};

export type InternalMessage = {
  id: number;
  from: User;
  to: User;
  message: string;
  date: Date;
  isRead: boolean;
};

export type KbbModel = {
  id: number;
  modelId: number;
  model: string;
  makeId: number;
  make: string;
  year: number;
};

export type KbbMake = {
  id: number;
  year: number;
  makeId: number;
  make: string;
};

export type KbbYear = {
  id: number;
  year: number;
};

export interface PaginatedResponse {
  currentPage: number;
  next: string | null;
  previous: string | null;
  totalPages: number;
  totalResults: number;
}

export type LaborRate = {
  id: number;
  name: string;
  rate: string;
  code: string;
  useMatrix: boolean;
  calculateSupplies: boolean;
  laborRateMatrix: LaborRateMatrix | null;
  defaultNoCharge: boolean;
  dmsCode: string | null;
  laborPriceCode: string | null;
};

export interface LaborRateResponse extends PaginatedResponse {
  results: LaborRate[];
}

export interface OpCodesSettingsResponse extends PaginatedResponse {
  results: OperationCode[];
}

export type LaborRateMatrix = {
  id: number;
};

export type LaborRateMatrixValue = {
  id: number;
  hours: number;
  price: number | null;
};

export interface LyMoney {
  amount: number;
  currency: string;
}

export interface LyStreetAddress {
  city: string;
  country: string;
  county: string;
  line1: string;
  line2: string;
  state: string;
  zip: string;
}

export interface LyHumanName {
  firstName: string;
  lastName: string;
  middleName: string;
  singularName: string;
  suffix: string;
  title: string;
}

export interface LyManagerUser {
  eventDescription: string;
  eventID: unknown | null;
  eventOccurrenceDateTime: unknown | null;
  factoryID: unknown | null;
  isActive: boolean;
  number: string;
  otherId: unknown | null;
  personalName: LyHumanName;
  specialRemarksDescription: string;
  ssn: string;
  streetAddress: LyStreetAddress;
  username: unknown | null;
}

// TODO no idea what's nullable
export interface LyFinanceInvoice {
  attributes: {
    advertisingSource: string;
    advertisingSourceCode: string;
    basePaymentAmount: LyMoney;
    baseVehicleGross: LyMoney;
    buy: {
      amountFinanced: LyMoney;
      balloonRate: number;
      beaconScore: number;
      deferredDownPayments: unknown[];
      deliveredPrice: LyMoney;
      deposit: LyMoney;
      fees: {
        bank: {
          fee: LyMoney;
        };
        dealerFees: unknown[];
        licensing: {
          fee: LyMoney;
        };
        loanOrigination: {
          fee: LyMoney;
        };
        loanProcessing: {
          fee: LyMoney;
        };
        proratedLicensing: {
          fee: LyMoney;
        };
        tradeLicensing: {
          fee: LyMoney;
        };
      };
      finalPayment: LyMoney;
      financeCharge: LyMoney;
      lsiVsi: LyMoney;
      paymentDescription: unknown | null;
      taxes: {
        dealerTaxes: unknown[];
      };
      totalAmountFinanced: LyMoney;
      tradeTaxCredit: LyMoney;
    };
    buyRate: number;
    cashDownPayment: LyMoney;
    city: string;
    cobuyer: {
      identity: {
        address1: string;
        address2: string;
        birthDate: unknown | null;
        city: string;
        citySubDivisionName: unknown | null;
        country: string;
        county: string;
        emailAddress: string;
        personalName: LyHumanName;
        phoneNumbers: unknown[];
        sex: string;
        spouseAddress1: string;
        spouseAddress2: string;
        spouseCity: string;
        spouseCountry: unknown | null;
        spouseCounty: string;
        spouseEmailAddress: string;
        spouseName: LyHumanName;
        spousePhoneNumbers: unknown[];
        spouseSsn: string;
        spouseState: string;
        spouseZip: string;
        ssn: unknown | null;
        state: string;
        zip: string;
      };
      isMarried: boolean;
      license: {
        number: string;
        state: string;
      };
      relationshipType: string;
    };
    contractDate: string;
    costAdds: unknown[];
    customerDriverLicense: {
      number: string;
      state: string;
    };
    customerType: string;
    customerTypeDetail: '-';
    dateApproved: unknown | null;
    dateCheckedOut: unknown | null;
    dateProcessed: unknown | null;
    dateSentToAccounting: string;
    dateSold: string;
    daysToFirstPayment: number;
    dealNumber: number;
    dealPurchaseProgram: string;
    dealStatus: string;
    dealStatusList: unknown | null;
    dealType: string;
    dealerRebateAmount: LyMoney;
    dealerState: string;
    dealerZip: string;
    decrLifePremium: LyMoney;
    discountOtherTotal: LyMoney;
    downPayment: LyMoney;
    eventOccurrenceDateTime: string;
    feeAmount: LyMoney;
    finalAmount: LyMoney;
    financeCharge: LyMoney;
    financeChargeAmount: LyMoney;
    financeDeptGross: LyMoney;
    financeDeptGrossWithHoldback: LyMoney;
    financeManagers: LyManagerUser[];
    financeReserve: LyMoney;
    firstPaymentDate: string;
    fleetAccountString: string;
    grossSellingPrice: LyMoney;
    hardAdds: unknown[];
    hardAddsTotal: LyMoney;
    incentives: unknown[];
    incentivesTotal: LyMoney;
    insuranceProducts: unknown[];
    interestDays: number;
    isApproved: boolean;
    isCheckedOut: boolean;
    isProcessed: boolean;
    isSentToAccounting: boolean;
    isSold: boolean;
    lease: {
      baseResidualAmount: LyMoney;
      capCostReduction: LyMoney;
      cashCapReductionAmount: LyMoney;
      cashRequired: LyMoney;
      estimatedOfficialFeeTax: LyMoney;
      factorInterestType: unknown | null;
      feeAmount: LyMoney;
      fees: {
        acquisition: {
          __value: unknown | null;
        };
        dealerFees: unknown[];
        disposition: {
          fee: LyMoney;
        };
        licensing: {
          fee: LyMoney;
          feeType: string;
        };
        proratedLicensing: {
          fee: LyMoney;
          feeType: string;
        };
        tradeLicensing: {
          fee: LyMoney;
          feeType: string;
        };
      };
      grossCapCost: LyMoney;
      hardAddsResidualAmount: LyMoney;
      hardAddsResidualPercentage: number;
      isOnePayLease: boolean;
      mileageActual: number;
      mileageAllowance: number;
      mileageDollarsPerMileLeaseEnd: LyMoney;
      mileageDollarsPerMileUpFront: LyMoney;
      mileageEstimate: number;
      milesUpFront: number;
      moneyFactor: number;
      monthlyDepreciationAmount: LyMoney;
      monthlyServiceCharge: LyMoney;
      netCapCost: LyMoney;
      otherMonthlyCharges: LyMoney;
      packageDiscounts: LyMoney;
      rebateCapReductionAmount: LyMoney;
      residualAmount: LyMoney;
      residualPercentage: number;
      securityDeposit: LyMoney;
      taxes: {
        dealerTaxes: unknown[];
        monthlyUse: LyMoney;
        property: LyMoney;
      };
      totalDueAtSigning: LyMoney;
      tracLease: boolean;
      tradeTaxCredit: LyMoney;
    };
    leasePrice: LyMoney;
    leaseRateMoneyFactorString: string;
    lenderAddress: string;
    lenderCity: string;
    lenderCode: string;
    lenderDealerNumber: string;
    lenderFedSSN: string;
    lenderName: string;
    lenderPhone: {
      digits: string;
      numberType: string;
    };
    lenderRemitTileName: unknown | null;
    lenderRemitTitleAddress: string;
    lenderRemitTitleCity: string;
    lenderRemitTitleName: string;
    lenderRemitTitleState: string;
    lenderRemitTitleZip: string;
    lenderState: string;
    lenderZip: string;
    manufacturerRebateAmount: LyMoney;
    maturityDateYearMonthDate: string;
    milesAtSaleDate: number;
    mrm: LyMoney;
    msrp: LyMoney;
    netAmountFinanced: LyMoney;
    netAnnualPercentageRate: number;
    netCapitalizedCostAmount: LyMoney;
    netTradeAllowance: LyMoney;
    netTradeAllowanceAmount: LyMoney;
    numberOfPaymentsNumeric: number;
    otherDeptGross: LyMoney;
    otherDeptGrossWithHoldback: LyMoney;
    paymentCount: number;
    prepaidFinanceChargeAmount: LyMoney;
    productDetails: unknown[];
    protectionPackages: unknown[];
    purchasePriceAmount: LyMoney;
    rate: number;
    rebates: unknown[];
    rebatesTotal: LyMoney;
    regularPayment: LyMoney;
    residualAmount: LyMoney;
    saleCategoryString: string;
    salesDeptGross: LyMoney;
    salesDeptGrossWithHoldback: LyMoney;
    salesGeneralManager: unknown[];
    salesManager: LyManagerUser;
    salesNumber: string;
    salesNumber2: unknown | null;
    salesPeople: unknown[];
    securityDepositAmount: LyMoney;
    sellingPrice: LyMoney;
    serviceContracts: unknown[];
    softAdds: unknown[];
    taxAmount: LyMoney;
    taxableSalesAmount: LyMoney;
    taxes: unknown[];
    temporaryTag: string;
    termMonths: number;
    totalDownPayment: LyMoney;
    totalOfPayments: LyMoney;
    totalReductionsAmount: LyMoney;
    totalTradeAllowance: LyMoney;
    transactionPrice: LyMoney;
    updateStamp: string;
    upsource: string;
    vehicleType: string;
  };
  dealKey: string;
  dealNumber: number;
  placement: {
    groupId: number;
    groupName: string;
    environmentId: number;
    environmentType: string;
    dealerId: number;
    sourceId: number;
    companyId: number;
    companyName: string;
    storeId: number;
    storeName: string;
  };
  references: {
    customerKey: string;
    inventoryKey: string;
    tradeIns: any[];
  };
}

export interface LyPartsInvoice {
  placement: {
    groupId: number;
    groupName: string;
    environmentId: number;
    environmentType: string;
    dealerId: number;
    sourceId: number;
    companyId: number;
    companyName: string;
    storeId: number;
    storeName: string;
    serviceLocationId: number;
    serviceLocationName: string;
  };
  attributes: {
    accountingDate: string;
    actualWholesaleAmount: LyMoney;
    address: string;
    balanceDue: LyMoney;
    city: string;
    closeDate: string;
    comments: string;
    coreAmount: LyMoney;
    cost: LyMoney;
    countyTaxAmount: LyMoney;
    customerFocusIncentives: unknown | null;
    customerPurchaseOrderNumber: string;
    dealerDiscountAmount: LyMoney;
    dealerNumber: unknown | null;
    deliveredDate: unknown | null;
    deliveryType: string;
    documentDate: string;
    documentID: string;
    footerText: unknown | null;
    freightCharge: LyMoney;
    freightTerms: unknown | null;
    headerText: unknown | null;
    invoiceDate: string;
    invoiceNumber: string;
    invoiceTask: unknown | null;
    invoiceType: unknown | null;
    isCod: boolean;
    isDelivered: boolean;
    isDelivery: boolean;
    isReturn: boolean;
    isTaxable: boolean;
    isWholesale: boolean;
    legalName: string;
    letterOfCredit: unknown | null;
    locationName: string;
    miscChargeTotal: LyMoney;
    modifiedDate: string;
    monthlyDiscountsOrAllowancesAmount: LyMoney;
    monthlyMiscSalesChargesAmount: LyMoney;
    monthlyPartsTotalAmount: LyMoney;
    monthlyTotalAmount: LyMoney;
    partNumbersStocked: unknown | null;
    partsDiscountAmount: LyMoney;
    partsInvoiceItemAmount: LyMoney;
    partsInvoiceLines: {
      actualWholesaleAmount: LyMoney;
      binLocation: string;
      caseCartonNumber: unknown | null;
      comments: string;
      conveyanceNumber: unknown | null;
      coreAmount: LyMoney;
      coreUnitAmount: LyMoney;
      dealerDiscountAmount: LyMoney;
      detailNotes: unknown | null;
      discountPercentage: unknown | null;
      eTADate: unknown | null;
      extendedAmount: LyMoney;
      extendedCoreAmount: LyMoney;
      freightCharge: number;
      grossDiscountAmount: LyMoney;
      hTSCode: unknown | null;
      headerNotes: unknown | null;
      linenumber: number;
      manufacturerOrderNumber: unknown | null;
      miscChargeTotal: LyMoney;
      miscDescription: unknown | null;
      orderDate: unknown | null;
      orderReferenceNumber: unknown | null;
      orderType: unknown | null;
      packQty: number;
      partClass: string;
      partKey: string;
      partName: string;
      partNumber: string;
      partsInvoiceItemNetAmount: LyMoney;
      priceDescription: string;
      processCode: unknown | null;
      processDate: unknown | null;
      purchaseOrderNum: string;
      quantityOrdered: number;
      quantityShipped: number;
      retailAmount: LyMoney;
      saleType: string;
      shipmentNumber: unknown | null;
      shippingPDC: unknown | null;
      soldAmount: LyMoney;
      soldDate: string;
      source: string;
      sourceDescription: string;
      specialTax: number;
      submittedAmount: LyMoney;
      taxAmount: LyMoney;
      taxDescription: string;
      taxPaidToName: string;
      tradeInAmount: LyMoney;
      vendor: string;
    }[];
    purchaseOrderNumber: string;
    referenceNumber: string;
    restockingFee: LyMoney;
    secondaryDealerNumber: unknown | null;
    shipMethod: string;
    shipTo: {
      address1: string;
      address2: string;
      birthDate: unknown | null;
      city: string;
      citySubDivisionName: unknown | null;
      country: string;
      county: string;
      emailAddress: string;
      personalName: {
        firstName: string;
        lastName: string;
        middleName: string;
        singularName: string;
        suffix: string;
        title: string;
      };
      phoneNumbers: unknown[];
      sex: string;
      spouseAddress1: string;
      spouseAddress2: string;
      spouseCity: string;
      spouseCountry: unknown | null;
      spouseCounty: string;
      spouseEmailAddress: string;
      spouseName: {
        firstName: string;
        lastName: string;
        middleName: string;
        singularName: string;
        suffix: string;
        title: string;
      };
      spousePhoneNumbers: unknown[];
      spouseSsn: string;
      spouseState: string;
      spouseZip: string;
      ssn: string;
      state: string;
      zip: string;
    };
    shipToLocationID: unknown | null;
    shipmentCarrierDetails: unknown[];
    shipmentNumber: string;
    soldBy: {
      eventDescription: string;
      eventID: unknown | null;
      eventOccurrenceDateTime: unknown | null;
      factoryID: unknown | null;
      isActive: boolean;
      number: string;
      otherId: unknown | null;
      personalName: {
        firstName: string;
        lastName: string;
        middleName: string;
        singularName: unknown | null;
        suffix: string;
        title: string;
      };
      specialRemarksDescription: string;
      ssn: string;
      streetAddress: {
        city: string;
        country: string;
        county: string;
        line1: string;
        line2: string;
        state: string;
        zip: string;
      };
      username: string;
    };
    state: string;
    status: number;
    subHeaderText: unknown | null;
    subTotal: LyMoney;
    taxDescription: string;
    taxOnTradeInTaxAmount: LyMoney;
    taxPercent: number;
    titleText: unknown | null;
    totalAmount: LyMoney;
    totalDiscountAmount: LyMoney;
    totalOtherAmount: LyMoney;
    totalQuantity: unknown | null;
    totalTaxAmount: LyMoney;
    tradeInAmount: LyMoney;
    yTDDiscountsOrAllowancesAmount: LyMoney;
    yTDMiscSalesChargesAmount: LyMoney;
    yTDPartsTotalAmount: LyMoney;
    yTDTotalAmount: LyMoney;
    zipcode: string;
  };
  partsInvoiceKey: string;
  references: {
    payments: {
      amount: LyMoney;
      createdStamp: string;
      description: string;
      glAccount: string;
      includedInBalance: boolean;
      payType: string;
      paymentKey: string;
      paymentMethodCode: string;
      username: string;
    }[];
    soldToCustomer: {
      placement: {
        groupId: number;
        groupName: string;
        environmentId: number;
        environmentType: string;
        dealerId: number;
        sourceId: number;
      };
      attributes: {
        allowsEmailContact: boolean;
        allowsMailContact: boolean;
        allowsPhoneContact: boolean;
        allowsSmsContact: boolean;
        comments: string;
        customerNumber: string;
        customerType: string;
        driverLicenseNumber: string;
        driverLicenseState: string;
        identity: {
          address1: string;
          address2: string;
          birthDate: string;
          city: string;
          citySubDivisionName: unknown | null;
          country: string;
          county: string;
          emailAddress: string;
          personalName: {
            firstName: string;
            lastName: string;
            middleName: string;
            singularName: unknown | null;
            suffix: string;
            title: string;
          };
          phoneNumbers: {
            digits: string;
            numberType: string;
          }[];
          sex: string;
          spouseAddress1: string;
          spouseAddress2: string;
          spouseCity: string;
          spouseCountry: unknown | null;
          spouseCounty: string;
          spouseEmailAddress: string;
          spouseName: {
            firstName: string;
            lastName: string;
            middleName: string;
            singularName: unknown | null;
            suffix: string;
            title: string;
          };
          spousePhoneNumbers: {
            digits: string;
            numberType: string;
          }[];
          spouseSsn: string;
          spouseState: string;
          spouseZip: string;
          ssn: string;
          state: string;
          zip: string;
        };
        isDealerToDealer: boolean;
        isMarried: boolean;
        isWholesale: boolean;
        lastContactDate: string;
        modifiedStamp: string;
        spouseName: unknown | null;
      };
      customerId: string;
      customerKey: string;
    };
  };
}

export interface LyRepairOrder {
  key: string;
  vin: string;
  year: number;
  make: string;
  miles: number;
  model: string;
  tagId: string;
  status: string;
  number: string;
  waiter: boolean;
  openDate: string;
  closedDate: any | null;
  pickupDate: any | null;
  rewardsUsed: number;
  initialValue: number;
  finalValue: number;
  recommendations: any[];
  customerPayValue: number;
  promisedDate: any | null;
  internal: boolean;
  hours: any | null;
  serialNumber: any | null;
  objectNumber: any | null;
  vehicleDmsId: string;
  balanceDue: number;
  advisor: {
    id: string;
    firstName: string;
    lastName: string;
    entityObject: any | null;
  };
  customer: {
    zip: string;
    city: string;
    name: string;
    email: string;
    state: string;
    address1: string;
    address2: string;
    doNotContact: boolean;
    marketingOptOut: boolean;
    allowEmailContact: boolean;
    allowSmsContact: boolean;
    dmsId: string;
  };
  technician: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export type MotorEstimate = any;

export type MotorEstimatedWorkTime = {
  baseLaborTime: number;
  name: string;
  applicationId: string;
  baseLaborTimeDescription: string;
  allLaborTime: number;
  allLaborTimeDescription: string;
  additionalLaborTime: number;
  additionalLaborDescription: any;
  laborTimeInterval: string;
  notes: string;
  requiredSkill: any;
  subGroupId: number;
  subGroupName: string;
  groupId: number;
  groupName: string;
  systemId: number;
  systemName: string;
  optional: any[];
  qualifiers: any[];
};

export type MyLoan = {
  id: number;
  repairOrder: RepairOrder;
  amount: any | null; // type is supposed to be "money"
  linkHash: string;
  terms: string | null;
  dateSent: Date | null;
  dateViewed: Date | null;
  dateCompleted: Date | null;
  status: string;
  loanId: string | null;
  applicationUrl: string | null;
  dateCreated: Date | null;
  actualAmount: any | null; // type is supposed to be "money"
  loanStatus: string | null;
  transactionId: string | null;
  transactedAmount: any | null; // type is supposed to be "money"
};

export type MpiTemplate = {
  id?: number;
  name: string;
  active?: boolean;
  mpiGroups: MpiGroup[];
  presetName: string | null;
};

export type MpiTemplates = MpiTemplate[];

export type MpiGroup = {
  id?: number;
  name: string;
  active?: boolean;
  mpiItems: MpiItem[];
};

export type MpiItem = {
  id?: number;
  mpiGroup?: MpiGroup;
  name: string;
  rangeMaximum?: number | null;
  rangeUnit?: string | null;
  value?: number | null;
  status?: MpiResultsStatus;
};

export type MpiItemOperationCode = {
  id: number;
  mpiItem: MpiItem;
  operationCode: OperationCode;
};

export type OperationCode = {
  id: number;
  dmsId: string | null;
  cause: string | null;
  chargeSupplies: boolean;
  code: string;
  complaint: string | null;
  correction: string | null;
  laborHours: number;
  laborPrice: number;
  laborPriceCode: string | null;
  laborTaxable: boolean;
  menuPartsPricing: boolean | null;
  menuPricing: boolean | null;
  partsPrice: number;
  partsTaxable: boolean;
  serviceLane: ServiceLanes;
  usedCount: number;
  defaultLaborRate: LaborRate | null; // get
  defaultLaborRateId?: number; // create and update
  customerNote?: string | null; // nhtsa consequence op code maping
  serviceGroupId: number | null;
  serviceGroup: ServiceGroup | null;
};

export type Part = {
  // because Reasons™
  part: Part;

  id: number;
  number: string | null;
  name: string;
  bin: string | null;
  available: number | null;
  price: number | null;
  replacesPartNumber: string | null;
  cost: string | null;
  list: string | null;
  trade: string | null;
  comp: string | null;
  exchange: string | null;
  price6: string | null;
  price7: string | null;
  price8: string | null;
  price9: string | null;
  price10: string | null;
  unit: string | null;
  core: string | null;
  retail: string | null;
  source: string | null;
  manufacturerCode: string | null;
  usedCount: number;
  vendor: string | null;
};

export type PartMatrix = {
  id: number;
  name: string;
  partMatrixValues: PartMatrixValue[] | null;
};

export type PartMatrixValue = {
  id: number;
  partMatrx: PartMatrix;
  min: string;
  max: string | null;
  priceField: string;
  priceBreak: string | null;
  markupPercentage: string;
  discountPercentage: string;
};

export type PartSource = {
  id: number;
  source: string;
};

export type PartSourceLaborRateMatrix = {
  id: number;
  partSource: PartSource;
  laborRate: LaborRate;
  partMatrix: PartMatrix;
};

export type Payment = {
  id: number;
  type: string | null;
  customer: Customer;
  number: string | null;
  amount: any;
  refundedAmount: any | null;
  linkHash: string | null;
  transactionId: string | null;
  referenceId: string | null;
  notes: string | null;
  description: string | null;
  status: string;
  cardType: string | null;
  cardNumber: string | null;
  dateCreated: Date;
  paymentInteractions: PaymentInteraction[];
  dmsReferenceId: string | null;
  dmsId: string | null;
  tenderType: string | null;
  pinStatement: string | null;
  dedicatedFileName: string | null;
  applicationLabel: string | null;
};

export type PaymentInteraction = {
  id: number;
  type: string;
  date: Date;
};

export type PaymentRefund = {
  id: number;
  payment: Payment;
  transactionId: string;
  user: User;
  refundAmount: any | null; // type is supposed to be "money"
  date: Date;
};

export type PaymentTerminal = {
  id: number;
  name: string;
  deviceId: string;
};

export type PriceMatrix = {
  id: number;
  hours: number;
  price: number | null;
};

export enum QuoteStatuses {
  NOT_STARTED = 'Not Started',
  ADVISOR_IN_PROGRESS = 'Advisor In Progress',
  TECHNICIAN_IN_PROGRESS = 'Technician In Progress',
  PARTS_IN_PROGRESS = 'Parts In Progress',
  SENT = 'Sent',
  VIEWED = 'Viewed',
  COMPLETED = 'Completed',
  CONFIRMED = 'Confirmed',
}

export enum QuotingTabs {
  EDIT_ADDITIONAL = 'EDIT_ADDITIONAL',
  EDIT_PRE_APPROVED = 'EDIT_PRE_APPROVED',
  QUOTE_VIEW = 'QUOTE_VIEW',
  RO_DETAILS = 'RO_DETAILS',
  QUOTE_DETAILS = 'QUOTE_DETAILS',
}

export type QuoteDiscount = {
  id: number;
  name: string | null;
  type: string | null;
  amount: string | null;
  maxDiscount: string | null;
};

export type QuoteMiscCharge = {
  id: number;
  name: string | null;
  type: string | null;
  amount: string | null;
  minCharge: string | null;
  maxCharge: string | null;
};

export type ReconditioningPhase = {
  id: number;
  name: string;
  color: string;
  textColor: string | null;
  sortOrder: number;
  special: boolean;
};

export type RepairOrderCustomer = {
  id: number;
  repairOrder: RepairOrder;
  customer: Customer;
};

export enum CheckInStatus {
  NOT_STARTED = 'Not Started',
  COMPLETE = 'Complete',
}

export enum CheckOutStatus {
  NOT_STARTED = 'Not Started',
  COMPLETE = 'Complete',
  PAID = 'Paid',
  SIGNED = 'Signed',
}

export type RepairOrder = {
  appointments: Appointment[];
  approvedValue: number | null;
  checkInStatus: CheckInStatus;
  checkOutStatus: CheckOutStatus;
  dateClosed: Date | null;
  dateCreated: Date;
  dateOfSale: Date | null;
  dateQuoteInProgress: Date | null;
  dmsKey: string | null;
  finalValue: number | null;
  hasVideo: boolean;
  hours: string | null;
  id: number;
  internal: boolean;
  linkHash: string;
  make: string | null;
  miles: number | null;
  model: string | null;
  mpiStatus: (typeof MpiStatus)[keyof typeof MpiStatus];
  mpiStatusTimestamp: Date | null;
  notes: RepairOrderNote[] | null;
  number: string;
  objectNumber: string | null;
  paymentStatus: string;
  paymentStatusTimestamp: Date | null;
  pickListPartStatus: string | null;
  pickListPartStatusTimestamp: Date | null;
  pickupDate: Date | null;
  primaryAdvisor: User | null;
  primaryCustomer: Customer;
  primaryPartsAdvisor: User | null;
  primaryTechnician: User | null;
  promisedDate: Date | null;
  quoteInProgress: User | null;
  quoteStatus: string;
  quoteStatusTimestamp: Date | null;
  reconditioningPhase: ReconditioningPhase | null;
  reconditioningPhaseTimestamp: Date | null;
  repairOrderCustomers: RepairOrderCustomer[] | null;
  repairOrderMPI: RepairOrderMpi | null;
  repairOrderQuote: RepairOrderQuote;
  repairOrderQuoteId: number | null;
  repairOrderReconditioningPhaseInteractions: RepairOrderReconditioningPhaseInteraction[] | null;
  repairOrderReview: RepairOrderReview;
  repairOrderServicePhaseInteractions: RepairOrderServicePhaseInteraction[] | null;
  repairOrderTeams: RepairOrderTeam[] | null;
  rewardsUsed: number;
  serialNumber: string | null;
  serviceLane: string | null;
  servicePhase: ServicePhase | null;
  servicePhaseTimestamp: Date | null;
  startValue: number | null;
  tagId: string | null;
  upgradeQueue: boolean;
  vehicleDmsId: string | null;
  videoStatus: string;
  videoStatusTimestamp: Date | null;
  vin: string | null;
  waiter: boolean;
  waiverSignature: string | null;
  waiverVerbiage: string | null;
  year: number | null;
};

export type RepairOrderImage = {
  id: number;
  repairOrder: RepairOrder;
  name: string;
  image: string;
  note: string | null;
  internal: boolean | null;
  appointment: Appointment;
  approvedValue: number;
  checkIn: CheckIn1;
  dateClosed: string;
  dateCreated: string;
  dateOfSale: string;
  dateQuoteInProgress: string;
  dmsKey: string;
  finalValue: number;
  hours: string;
  linkHash: string;
  make: string;
  miles: number;
  model: string;
  mpiStatus: string;
  mpiStatusTimestamp: string;
  notes: RepairOrderNote[];
  number: string;
  objectNumber: string;
  paymentStatus: string;
  paymentStatusTimestamp: string;
  pickListPartStatus: string;
  pickListPartStatusTimestamp: string;
  pickupDate: string;
  primaryAdvisor: User;
  primaryCustomer: Customer;
  primaryPartsAdvisor: User;
  primaryTechnician: User;
  promisedDate: string;
  quoteInProgress: User;
  quoteStatus: string;
  quoteStatusTimestamp: string;
  reconditioningPhase: ReconditioningPhase;
  reconditioningPhaseTimestamp: string;
  repairOrderCustomers: any[]; // TODO - are these actually RepairOrderCustomers or just Customers?
  repairOrderMPI: RepairOrderMpi;
  repairOrderQuote: RepairOrderQuote;
  repairOrderQuoteId: number | null;
  repairOrderReconditioningPhaseInteractions: RepairOrderReconditioningPhaseInteraction[];
  repairOrderReview: RepairOrderReview;
  repairOrderServicePhaseInteractions: RepairOrderServicePhaseInteraction[];
  repairOrderTeams: RepairOrderTeam[];
  rewardsUsed: number;
  serialNumber: string;
  serviceLane: string;
  servicePhase: ServicePhase;
  servicePhaseTimestamp: string;
  startValue: number;
  tagId: string;
  upgradeQueue: boolean;
  videoStatus: string;
  videoStatusTimestamp: string;
  vin: string;
  waiter: boolean;
  waiverSignature: string;
  waiverVerbiage: string;
  year: number;
};

export type RepairOrderInteraction = {
  id: number;
  user: User | null;
  customer: Customer | null;
  type: string;
  date: Date;
};

export type RepairOrderMpi = {
  id: number;
  repairOrder: RepairOrder;
  dateCompleted: Date | null;
  dateSent: Date | null;
  results: string | null;
  repairOrderMPIInteractions: RepairOrderMpiInteraction[] | null;
  dateViewed: Date | null;
};

export type RepairOrderMpiInteraction = {
  id: number;
  user: User | null;
  customer: Customer | null;
  type: string;
  date: Date;
};

export type RepairOrderNote = {
  id: number;
  note: string | null;
  dateCreated: Date;
  user: User | null;
};

export type RepairOrderQuote = {
  id: number;
  dateCreated: Date;
  dateSent: Date | null;
  dateCustomerViewed: Date | null;
  dateCompleted: Date | null;
  dateConfirmed: Date | null;
  etag: string | null;
  repairOrderQuoteRecommendations: RepairOrderQuoteRecommendation[];
  status: QuoteStatuses;
  subtotal: number | null;
  tax: number | null;
  processingFee: number | null;
  total: number | null;
  customerTotal: number | null;
  customerTotalTax: number | null;
  partsTax: number | null;
  laborTax: number | null;
  repairOrderQuoteLogs: RepairOrderQuoteLog[];
  deleted: boolean;
  supplies: number | null;
  customer: Customer | null;
  year: number | null;
  make: string | null;
  model: string | null;
  vin: string | null;
  motorBaseVehicleId: string | null;
  motorSubModelId: string | null;
  motorEngineId: string | null;
  repairOrderQuoteMiscCharges: Charge[];
  repairOrderQuoteDiscounts: Discount[];
  discountAmount: number | null;
  miscChargeAmount: number | null;
  laborPrice: number | null;
  partsPrice: number | null;
  laborHours: number | null;
  approvedLaborPrice: number | null;
  approvedLaborHours: number | null;
  approvedPartsPrice: number | null;
  approvedSuppliesPrice: number | null;
  approvedTax: number | null;
  approvedTotal: number | null;
  approvedCustomerTotal: number | null;
  approvedDiscountTotal: number | null;
  approvedMiscChargesTotal: number | null;
  // these last 3 aren't in the php file from the backend so will need to confirm
  repairOrderId: number;
  totalDiscounts: number;
  totals: {
    approved: RepairOrderQuoteTotals;
    preApproved: RepairOrderQuoteTotals;
    additional: RepairOrderQuoteTotals;
    all: RepairOrderQuoteTotals;
  };
  eTag?: string;
};

export type RepairOrderQuoteDiscount = {
  id: number;
  repairOrderQuote: RepairOrderQuote | null;
  repairOrderQuoteRecommendation: RepairOrderQuoteRecommendation | null;
  name: string;
  amount: number;
  discountAmount: number;
  type: string | null;
  maxDiscount: string | null;
  taxable: boolean;
};

export type MotorPart = {
  price: number;
  partNumber: string;
  name: string;
  position: string;
  manufacturer: any;
  quantity: number;
  qualifiers: Qualifier[];
  applicationId: string;
};

export type Qualifier = {
  description: string;
  active: boolean;
};

export type RepairOrderQuoteApprovalStatus = boolean | null;

export type RepairOrderQuoteLog = {
  id: number;
  user: User | null;
  customer: Customer | null;
  date: string;
  data: string | null; // JSON
};

export type RepairOrderQuoteRecommendation = {
  id: number;
  repairOrderQuote: RepairOrderQuote;
  operationCode: string;
  preApproved: boolean | null;
  approved: boolean | null;
  partsPrice: number | null;
  laborPrice: number | null;
  laborTax: number | null;
  partsTax: number | null;
  laborHours: number | null | string;
  laborTaxable: boolean | null;
  partsTaxable: boolean | null;
  notes: string | null;
  repairOrderQuoteRecommendationParts: RepairOrderQuoteRecommendationPart[] | null;
  noCharge: boolean | null;
  laborRate: LaborRate | null;
  menuPricing: boolean;
  chargeSupplies: boolean;
  complaint: string | null;
  cause: string | null;
  correction: string | null;
  discountAmount: number | null;
  miscChargeAmount: number | null;
  repairOrderQuoteMiscCharges: RepairOrderQuoteMiscCharge[] | null;
  repairOrderQuoteDiscounts: RepairOrderQuoteDiscount[] | null;
  sortOrder: number | null;
  motorApplicationId: number | null;
  subtotal: number | null;
  total: number | null;
  dmsId: string | null;
  isCustomPartsPrice: boolean;
  // these last 2 aren't in the php file from the backend so will need to confirm
  laborRateId?: number; // for create
  repairOrderQuoteId: number; // for create
};

export interface RepairOrderQuoteRecommendationCreate
  extends Omit<
    RepairOrderQuoteRecommendation,
    | 'approved'
    | 'discountAmount'
    | 'dmsId'
    | 'id'
    | 'laborPrice'
    | 'laborRate'
    | 'laborTax'
    | 'miscChargeAmount'
    | 'partsPrice'
    | 'partsTax'
    | 'repairOrderQuoteRecommendationParts'
    | 'repairOrderQuoteDiscounts'
    | 'repairOrderQuoteMiscCharges'
    | 'sortOrder'
    | 'repairOrderQuote'
    | 'subtotal'
  > {
  approved?: boolean;
  laborRateId?: number;
  operationCodeId?: number;
  repairOrderQuoteId: number;
}

export type RepairOrderQuoteRecommendationPart = {
  id: number;
  repairOrderRecommendation: RepairOrderQuoteRecommendation;
  part: Part | null;
  number: string | null;
  name: string | null;
  price: number | null;
  quantity: number | null;
  totalPrice: number | null;
  bin: string | null;
  expectedArrival: string | null;
  expectedArrivalUpdatedDate: Date | null;
  ordered: boolean;
  arrived: boolean;
  partCondition: string | null;
  manufacturerCode: string | null;
  motorApplicationId: number | null;
  vendor: string | null | null;
};

export type RepairOrderQuoteTotals = {
  customerTotal: number;
  customerTotalTax: number;
  discountAmount: number;
  discounts: number;
  labor: number;
  laborHours: number;
  laborTax: number;
  miscChargeAmount: number;
  miscCharges: number;
  parts: number;
  partsTax: number;
  processingFee: number;
  subTotal: number;
  supplies: number;
  suppliesTax: number;
  tax: number;
  total: number;
};

export type RepairOrderReconditioningPhaseInteraction = Record<string, never>;

export type RepairOrderReview = {
  id: number;
  repairOrder: RepairOrder;
  status: string;
  dateSent: Date | null;
  dateViewed: Date | null;
  dateCompleted: Date | null;
  rating: string | null;
  platform: string | null;
};

export type RepairOrderServicePhaseInteraction = Record<string, never>;

export type RepairOrderTeam = {
  id: number;
  user: User | null;
  repairOrder: RepairOrder;
};

export type RepairOrderPayment = {
  id: number;
  repairOrder: RepairOrder | null;
  amount: any; // type is supposed to be "money"
  transactionId: string | null;
  refundedAmount: string | null;
  dateCreated: Date;
  dateSent: Date | null;
  dateViewed: Date | null;
  datePaid: Date | null;
  dateConfirmed: Date | null;
  dateRefunded: Date | null;
  dateDeleted: Date | null;
  cardType: string | null;
  cardNumber: string | null;
  status: string | null;
  interactions: RepairOrderInteraction[];
  dmsId: string | null;
  tenderType: string | null;
  pinStatement: string | null;
  dedicatedFileName: string | null;
  applicationLabel: string | null;
  refundTransactionId: string | null;
};

export type RepairOrderPaymentRefund = {
  id: number;
  repairOrderPayment: RepairOrderPayment;
  transactionId: string;
  user: User | null;
  refundAmount: any; // type is supposed to be "money"
  date: Date;
};

export type RepairOrderPickListPart = {
  id: number;
  operationCode: string;
  partNumber: string;
  partName: string;
  partBin: string | null;
  price: number | null;
  quantity: number;
  status: string | null;
  deleted: boolean;
};

export type RepairOrderPickListPartInteraction = {
  id: number;
  user: User | null;
  custmoer: Customer | null;
  date: Date;
  status: string;
};

export type RepairOrderQuoteInteraction = {
  id: number;
  repairOrderQuote: RepairOrderQuote;
  user: User | null;
  customer: Customer | null;
  type: string;
  date: Date;
};

export type RepairOrderQuoteMiscCharge = {
  id: number;
  repairOrderQuote: RepairOrderQuote | null;
  repairOrderQuoteRecommendation: RepairOrderQuoteRecommendation | null;
  name: string;
  chargeAmount: number;
  deleted: boolean;
  type: string | null;
  minCharge: string | null;
  maxCharge: string | null;
  amount: number;
  taxable: boolean;
};

export type RepairOrderReviewInteractions = {
  id: number;
  repairOrderReview: RepairOrderReview;
  user: User | null;
  customer: Customer | null;
  type: string | null;
};

export type RepairOrderRewards = {
  id: number;
  repairOrder: RepairOrder | null;
  customer: Customer;
  date: Date;
  price: string;
};

export const RepairOrderMediaType = {
  image: 'IMAGE',
  video: 'VIDEO',
} as const;

export type RepairOrderMediaTypeKey = keyof typeof RepairOrderMediaType;
export type RepairOrderMediaValue = (typeof RepairOrderMediaType)[RepairOrderMediaTypeKey];

export type RepairOrderVideo = {
  id: number;
  path: string;
  status: string;
  interactions: RepairOrderInteraction[];
  duration: number | null;
  type?: RepairOrderMediaValue;
};

export type RepairOrderUvEyeUrl = {
  id: number;
  date: Date;
  expirationDate: Date | null;
  inspectionDate: Date | null;
  url: string;
  repairOrder: RepairOrder;
};

export type Rewards = {
  id: number;
  customer: Customer;
  repairOrder: RepairOrder;
  points: number;
  spent: boolean;
  date: Date;
  rewards: number;
};

export type ServicePhase = {
  id: number;
  name: string;
  color: string;
  textColor: string | null;
  sortOrder: number;
  special: boolean;
  sendSms: boolean;
  smsMessage: string;
};

export type ServiceOffHour = {
  id: number;
  description: string;
  startDate: Date;
  endDate: Date;
};

export type ServiceQuickSms = {
  id: number;
  user: User | null;
  message: string;
};

export type Sms = {
  id: number;
  user: User | null;
  customer: Customer | null;
  phone: string;
  message: string;
  incoming: boolean;
  isRead: boolean;
  date: Date;
  sid: string | null;
  status: string | null;
  mmsContent: string | null;
  type: string | null;
  automated: boolean;
  readBy: User | null;
  readDate: Date | null;
  incomingToPhone: string | null;
  from: User | null;
};

export type ScheduledMaintenance = {
  name: string;
  frequencyType: string;
  intervalMiles: number;
  intervalMonths: number;
  estimates: Estimate[];
};

export type ServiceExpress = {
  id: number;
  name: string;
  color: string;
  textColor: string;
  sortOrder: number;
  special: boolean;
  sendSms: boolean;
  smsMessage: string;
  serviceExpressRepairOrders: any;
};

export type ServiceLaneCapacity = {
  id: number;
  type: string;
  time: string;
  sundayHours: string;
  mondayHours: string;
  tuesdayHours: string;
  wednesdayHours: string;
  thursdayHours: string;
  fridayHours: string;
  saturdayHours: string;
};

export type Setting = {
  key: string;
  value: string | null;
};

export type SettingsEnvironment = {
  dmsEnv: boolean;
  dealerbuiltLocationId: boolean;
  nmiPassword: boolean;
  timezone: boolean;
  sparrowDealerId: boolean;
  automateEndpointId: boolean;
  cdkDealerId: boolean;
  cdkSubscriptionId: boolean;
  dealertrackCompany: boolean;
  dealertrackEnterprise: boolean;
  dealerbuiltSourceId: boolean;
  nmiUsername: boolean;
  heartlandApiKey: boolean;
  finmktUsername: boolean;
  finmktPassword: boolean;
  enableCommandLogger: boolean;
  supportEmail: boolean;
  sparrowApiUrl: boolean;
  uveyeApiUrl: boolean;
  uveyeApiKey: boolean;
  autosoftDealerCode: boolean;
};

export type SmsThread = {
  id: number;
  name: string;
  phone: string;
  repairOrderID: number;
  repairOrderNumber: string;
  newestRoAdvisorId: number;
  newestRoClosedDate: string;
  newestTextId: number;
  message: string;
  date: string;
  newestOutgoingAdvisorId: number;
  newestOutgoingDate: string;
  totalResults: number;
  unread: number;
  priority: number;
};

export type TransportationType = {
  id: number;
  title: string;
  description: string | null;
  internal: boolean | null;
};

export type UnreadMessageCounts = {
  internal: number;
  sms: number;
  service: number;
  sales: number;
};

export type User = {
  id: number;
  firstName: string;
  lastName: string;
  email: string | null;
  username: string | null;
  phone: string;
  role: UserRoles;
  certification: string | null;
  experience: string | null;
  securityQuestion: string | null;
  lastLogin: Date | null;
  active: boolean;
  processRefund: boolean;
  shareRepairOrders: boolean;
  dmsId: string | null;
  externalAuthentication: boolean;
  allowEditProcessingFees: boolean;
  allowManualPayments: boolean;
  autoSendVideo: boolean;
  passwordResetRequired: boolean;
};

export type Upgrade = {
  id: number;
  dateCreated: Date | null;
  dateSent: Date | null;
  dateCompleted: Date | null;
  valueMinimum: number | null;
  valueMaximum: number | null;
  trim: string | null;
  status: string;
  vehicleCondition: string | null;
  vehicle_id: number | null;
};

// TODO use this instead of UserRoles constant
export enum UserRoles {
  ADMIN = 'ROLE_ADMIN',
  FINANCE_MANAGER = 'ROLE_FINANCE_MANAGER',
  GLOBAL_PAY = 'ROLE_GLOBAL_PAY',
  PARTS_ADVISOR = 'ROLE_PARTS_ADVISOR',
  SALES_AGENT = 'ROLE_SALES_AGENT',
  SALES_MANAGER = 'ROLE_SALES_MANAGER',
  SERVICE_ADVISOR = 'ROLE_SERVICE_ADVISOR',
  SERVICE_MANAGER = 'ROLE_SERVICE_MANAGER',
  TECHNICIAN = 'ROLE_TECHNICIAN',
  NONE = '',
}

export const PaymentValidationWarningStates = ['Paid', 'Refunded', 'Confirmed'];

export type Media = {
  dateUploaded: string;
  id: number;
  media: string;
  note: string | null;
  type: string;
};

export enum GuideModes {
  NONE = 'None',
  READONLY = 'Read-only',
  ADD_TO_QUOTE = 'Add to Quote',
}

export enum MpiResultsStatus {
  red = 'red',
  yellow = 'yellow',
  green = 'green',
  grey = 'grey',
}

export interface MpiResultsItem {
  name: string;
  status: MpiResultsStatus;
  value?: number | null;
  text?: string;
  itemId?: number;
  hasRange?: boolean;
  special?: boolean; // same as hasRange
}

export interface MpiResultsGroup {
  group: string;
  items: MpiResultsItem[];
  groupId?: number;
}

export interface MpiResults {
  name: string;
  presetName?: string | null;
  results: MpiResultsGroup[];
  templateId?: number;
  notes?: string;
}
